import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { Box, Stack, Typography } from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemValidationTextField } from "../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import { SettingsSection } from "../../../components/UIToolkit/molecules/SettingsSection";
import SystemDialog from "../../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import type { PbjExcludedEmployeeRow } from "../../../utils/models";
import { usePbjExcludedEmployees, usePbjExcludeEmployee } from "../hooks/usePbjAudit";
import { dataTableSx, sectionCardSx } from "../PredictionTab/colors";
import { ExcludeEmployeeDialog } from "./ExcludeEmployeeDialog";
import { ImportExclusionsDialog } from "./ImportExclusionsDialog";

type Props = {
	startDate: string;
	endDate: string;
};

/** One row per excluded employee (facility-group scoped); pbj id keys the exclusion. */
const rowId = (r: PbjExcludedEmployeeRow) => r.pbjEmployeeId;

// "Excluded At" renders in the DEPARTMENT's timezone (not the viewer's), consistent with the other
// PBJ audit timestamps (see ShiftDurationReviewSection.formatReviewDateTime). The value is a tz-aware
// timestamptz, so the instant is correct either way — this just controls which wall-clock is shown.
const formatDateTime = (iso: string | null, zone: string): string => {
	if (!iso) return "—";
	// DEFAULT: an empty zone (the ChosenDepartment placeholder before the dept loads) falls back to the
	// viewer's zone rather than dropping the timestamp — the audit query is gated on a loaded dept anyway.
	const dt = zone ? DateTime.fromISO(iso).setZone(zone) : DateTime.fromISO(iso);
	return dt.isValid ? dt.toLocaleString(DateTime.DATETIME_MED) : "—";
};

/**
 * PBJ Audit → "Excluded from PBJ": the roster of employees flagged out of the CMS filing. Each row shows
 * who/why/when and the hours dropped for the viewed quarter. Inline actions edit the (mandatory, non-blank)
 * reason or un-exclude the employee; a toolbar "Bulk import" opens the Name + Reason upload/preview flow.
 * Reads live via `usePbjExcludedEmployees`; edits go through `usePbjExcludeEmployee` (invalidates the audit
 * family, so the row updates/disappears without a manual refresh).
 */
export const ExcludedFromPbjTable: FC<Props> = ({ startDate, endDate }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	const { data, isLoading, isError } = usePbjExcludedEmployees(startDate, endDate);
	const excludeMutation = usePbjExcludeEmployee();

	const [importOpen, setImportOpen] = useState(false);
	const [excludeOpen, setExcludeOpen] = useState(false);
	// Edit-reason dialog: the target row + the in-progress reason draft.
	const [editRow, setEditRow] = useState<PbjExcludedEmployeeRow | null>(null);
	const [reasonDraft, setReasonDraft] = useState("");
	const [reasonSubmitted, setReasonSubmitted] = useState(false);
	// Un-exclude confirm dialog target.
	const [unexcludeRow, setUnexcludeRow] = useState<PbjExcludedEmployeeRow | null>(null);

	const openEdit = useCallback((row: PbjExcludedEmployeeRow) => {
		setEditRow(row);
		setReasonDraft(row.reason ?? "");
		setReasonSubmitted(false);
	}, []);

	const closeEdit = useCallback(() => {
		if (excludeMutation.isPending) return;
		setEditRow(null);
		setReasonDraft("");
		setReasonSubmitted(false);
	}, [excludeMutation.isPending]);

	const trimmedReason = reasonDraft.trim();
	const reasonValid = trimmedReason.length > 0;

	// Edit-reason confirm: re-assert the exclusion with the new (non-blank) reason. Enforce non-blank
	// client-side before calling — the button is disabled when blank, and this is the final guard.
	const handleSaveReason = useCallback(() => {
		setReasonSubmitted(true);
		if (!editRow || !reasonValid || excludeMutation.isPending) return;
		excludeMutation.mutate({ departmentId, pbjEmployeeId: editRow.pbjEmployeeId, exclude: true, reason: trimmedReason }, { onSuccess: () => closeEdit() });
	}, [editRow, reasonValid, excludeMutation, departmentId, trimmedReason, closeEdit]);

	// Un-exclude: clear the flag (reason nulled server-side).
	const handleUnexclude = useCallback(() => {
		if (!unexcludeRow || excludeMutation.isPending) return;
		excludeMutation.mutate(
			{ departmentId, pbjEmployeeId: unexcludeRow.pbjEmployeeId, exclude: false, reason: null },
			{ onSuccess: () => setUnexcludeRow(null) },
		);
	}, [unexcludeRow, excludeMutation, departmentId]);

	const columns: GridColDef<PbjExcludedEmployeeRow>[] = useMemo(
		() => [
			{ field: "name", headerName: t("pbj_model.audit.excluded.cols.name"), minWidth: 160, flex: 1 },
			{ field: "pbjEmployeeId", headerName: t("pbj_model.audit.excluded.cols.pbj_id"), minWidth: 120 },
			{ field: "departmentId", headerName: t("pbj_model.audit.excluded.cols.department"), minWidth: 140, flex: 1 },
			{ field: "reason", headerName: t("pbj_model.audit.excluded.cols.reason"), minWidth: 200, flex: 1.5, valueGetter: (v: string | null) => v ?? "—" },
			{ field: "excludedBy", headerName: t("pbj_model.audit.excluded.cols.excluded_by"), minWidth: 160, flex: 1, valueGetter: (v: string | null) => v ?? "—" },
			{
				field: "excludedAt",
				headerName: t("pbj_model.audit.excluded.cols.excluded_at"),
				minWidth: 170,
				valueGetter: (v: string | null) => formatDateTime(v, chosenDepartment.timezone),
			},
			{
				field: "excludedHours",
				headerName: t("pbj_model.audit.excluded.cols.excluded_hours"),
				minWidth: 130,
				type: "number",
				align: "left",
				headerAlign: "left",
				valueFormatter: (v: number) => (v == null ? "" : v.toFixed(2)),
			},
			{
				field: "actions",
				headerName: t("pbj_model.audit.excluded.cols.actions"),
				minWidth: 110,
				sortable: false,
				filterable: false,
				disableExport: true,
				renderCell: (params: GridRenderCellParams<PbjExcludedEmployeeRow>) => (
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, height: "100%" }}>
						<SystemButton
							buttonVariant="borderless"
							size="small"
							onClick={() => openEdit(params.row)}
							leadingIcon={<EditOutlinedIcon sx={{ fontSize: 18 }} />}
							tooltip={t("pbj_model.audit.excluded.edit_reason")}
							aria-label={t("pbj_model.audit.excluded.edit_reason")}
							noFullWidthMobile
							data-testid={`pbj-excluded-edit-${params.row.pbjEmployeeId}`}
						/>
						<SystemButton
							buttonVariant="borderless"
							size="small"
							onClick={() => setUnexcludeRow(params.row)}
							leadingIcon={<PersonRemoveOutlinedIcon sx={{ fontSize: 18 }} />}
							tooltip={t("pbj_model.audit.excluded.unexclude")}
							aria-label={t("pbj_model.audit.excluded.unexclude")}
							noFullWidthMobile
							data-testid={`pbj-excluded-unexclude-${params.row.pbjEmployeeId}`}
						/>
					</Box>
				),
			},
		],
		[t, openEdit, chosenDepartment.timezone],
	);

	// pbj ids already excluded — passed to the exclude dialog's picker so an employee can't be double-excluded.
	const excludedPbjIds = useMemo(() => new Set((data ?? []).map((r) => r.pbjEmployeeId)), [data]);

	const excludeEmployeeButton = (
		<SystemButton
			buttonVariant="secondary"
			size="small"
			onClick={() => setExcludeOpen(true)}
			leadingIcon={<PersonAddAltOutlinedIcon sx={{ fontSize: 18 }} />}
			noFullWidthMobile
			data-testid="pbj-excluded-exclude-employee-button"
		>
			{t("pbj_model.audit.excluded.exclude_employee")}
		</SystemButton>
	);

	const bulkImportButton = (
		<SystemButton
			buttonVariant="secondary"
			size="small"
			onClick={() => setImportOpen(true)}
			leadingIcon={<UploadFileOutlinedIcon sx={{ fontSize: 18 }} />}
			noFullWidthMobile
			data-testid="pbj-excluded-bulk-import-button"
		>
			{t("pbj_model.audit.excluded.bulk_import")}
		</SystemButton>
	);

	const toolbarButtons = (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
			{excludeEmployeeButton}
			{bulkImportButton}
		</Box>
	);

	return (
		<>
			<SettingsSection
				layout="stack"
				title={t("pbj_model.audit.excluded.section.title")}
				description={t("pbj_model.audit.excluded.section.subtitle")}
				dataTestId="pbj-excluded-section"
				sx={(theme) => sectionCardSx(theme)}
			>
				<SystemDataGrid<PbjExcludedEmployeeRow>
					rows={data ?? []}
					columns={columns}
					getRowId={rowId}
					searchableColumns={["name", "pbjEmployeeId", "reason", "excludedBy"]}
					searchPlaceholder={t("pbj_model.audit.excluded.search")}
					loading={isLoading}
					error={isError}
					errorMessage={t("pbj_model.audit.excluded.failed")}
					defaultPageSize={25}
					containedHeight
					leadingToolbarNodes={toolbarButtons}
					emptyState={
						<EmptyState
							title={t("pbj_model.audit.excluded.empty_title")}
							hint={t("pbj_model.audit.excluded.empty_hint")}
							action={toolbarButtons}
							dataTestId="pbj-excluded-empty"
						/>
					}
					sx={(theme) => dataTableSx(theme)}
					dataTestId="pbj-model-excluded-table"
				/>
			</SettingsSection>

			<ExcludeEmployeeDialog
				open={excludeOpen}
				onClose={() => setExcludeOpen(false)}
				departmentId={departmentId}
				startDate={startDate}
				endDate={endDate}
				excludedPbjIds={excludedPbjIds}
			/>

			<ImportExclusionsDialog open={importOpen} onClose={() => setImportOpen(false)} departmentId={departmentId} />

			<SystemDialog
				open={editRow !== null}
				onClose={closeEdit}
				title={t("pbj_model.audit.excluded.edit_dialog.title", { name: editRow?.name ?? "" })}
				body={
					<Stack spacing={1} sx={{ pt: 1, minWidth: { xs: "auto", sm: 380 } }}>
						<Typography variant="body2" color="text.secondary">
							{t("pbj_model.audit.excluded.edit_dialog.description")}
						</Typography>
						<SystemValidationTextField
							value={reasonDraft}
							onChange={(e) => setReasonDraft(e.target.value)}
							label={t("pbj_model.audit.excluded.edit_dialog.reason_label")}
							placeholder={t("pbj_model.audit.excluded.edit_dialog.reason_placeholder")}
							required
							fullWidth
							submitTriggerred={reasonSubmitted}
							validation={{
								performValidation: "onlyAfterInitialChange",
								showValidWhenValid: "never",
								validationCallback: (v) => v.trim().length > 0,
								helperText: { invalid: t("pbj_model.audit.employees.exclusion.reason_required") },
							}}
							dataTestId="pbj-excluded-edit-reason-input"
						/>
					</Stack>
				}
				confirm={t("save")}
				confirmButtonVariant="primary"
				confirmDisabled={!reasonValid}
				confirmLoading={excludeMutation.isPending}
				onConfirm={handleSaveReason}
				cancel={t("cancel")}
				dataTestId="pbj-excluded-edit-dialog"
			/>

			<SystemDialog
				open={unexcludeRow !== null}
				onClose={() => (excludeMutation.isPending ? undefined : setUnexcludeRow(null))}
				title={t("pbj_model.audit.excluded.unexclude_dialog.title", { name: unexcludeRow?.name ?? "" })}
				body={t("pbj_model.audit.excluded.unexclude_dialog.body")}
				confirm={t("pbj_model.audit.excluded.unexclude")}
				confirmButtonVariant="primary"
				confirmLoading={excludeMutation.isPending}
				onConfirm={handleUnexclude}
				cancel={t("cancel")}
				dataTestId="pbj-excluded-unexclude-dialog"
			/>
		</>
	);
};
